/**
 * Turns one backend scoring entry into the values the card report shows:
 * the rules text the scorer matched, the parsed semantics behind it, and the
 * flags, numbers, and cost breakdowns that produced the score.
 */
import { formatNumber } from '../cardStats/formatters';
import { buildEffectSummary, effectTypeLabel } from './cardInfoModel';

// Which additional-cost keys the backend emits on castability / activation.
const ADDITIONAL_COST_LABELS = {
    sacrificeSelf: 'Sac self',
    sacrificePermanent: 'Sac permanent',
    payLife: 'Pay life',
    discardCard: 'Discard card',
    discardSelf: 'Discard self',
    exileCard: 'Exile card',
    exileSelf: 'Exile self',
    tapSelf: 'Tap self',
    isDiceRoll: 'Dice roll',
    isSuspend: 'Suspend',
};

// The bias components that feed `overallBias`. Every one that isn't the
// neutral 1.0 is shown, so the reader can see which factor moved the needle.
const BIAS_COMPONENT_LABELS = {
    castabilityBias: 'Castability',
    preConditionBias: 'Pre-condition',
    activationBias: 'Activation',
    upkeepBias: 'Upkeep',
};

/**
 * Cross-references a scoring entry's `contextIds` against the card's parsed
 * context tree so the report can show the exact (trigger, result) rules-text
 * pair the backend matched. Returns whichever parts resolve.
 */
export function resolveScoringContext(context, contextIds) {
    if (!context || !contextIds) return null;
    let matchedLine = null;
    let matchedPart = null;
    let matchedPreCondition = null;
    let matchedCondition = null;
    for (const line of Object.values(context.lines || {})) {
        if (!line) continue;
        if (contextIds.line && line.id !== contextIds.line) continue;
        matchedLine = matchedLine || line;
        for (const part of line.parts || []) {
            if (contextIds.part && part?.id !== contextIds.part) continue;
            matchedPart = matchedPart || part;
            for (const cond of part?.conditions || []) {
                if (contextIds.preCondition && cond?.id === contextIds.preCondition) matchedPreCondition = cond;
                for (const res of cond?.results || []) {
                    if (contextIds.condition && res?.id === contextIds.condition) {
                        matchedCondition = res;
                        matchedPreCondition = matchedPreCondition || cond;
                    }
                }
            }
        }
    }
    return {
        line: matchedLine,
        part: matchedPart,
        preCondition: matchedPreCondition,
        condition: matchedCondition,
    };
}

function collectAdditionalCostChips(block, keyPrefix) {
    if (!block?.additionalCosts) return [];
    const out = [];
    for (const [field, label] of Object.entries(ADDITIONAL_COST_LABELS)) {
        if (block.additionalCosts[field]) out.push({ key: `${keyPrefix}-${field}`, label });
    }
    const qualifiers = Number(block.additionalCosts.qualifiers || 0);
    if (qualifiers > 0) {
        out.push({
            key: `${keyPrefix}-qualifiers`,
            label: `+${qualifiers} qualifier${qualifiers === 1 ? '' : 's'}`,
        });
    }
    const loyaltyCost = block.additionalCosts.loyaltyCost;
    if (loyaltyCost != null && loyaltyCost !== 0) {
        out.push({
            key: `${keyPrefix}-loyalty`,
            label: `Loyalty ${loyaltyCost > 0 ? '+' : ''}${loyaltyCost}`,
        });
    }
    const suspendLength = Number(block.additionalCosts.suspendLength || 0);
    if (suspendLength > 0)
        out.push({
            key: `${keyPrefix}-suspend-len`,
            label: `Suspend ${suspendLength}`,
        });
    const freeCaveat = Number(block.additionalCosts.freeWithCaveat?.qualifiers || 0);
    if (freeCaveat > 0)
        out.push({
            key: `${keyPrefix}-free-caveat`,
            label: `Free caveat ×${freeCaveat}`,
        });
    return out;
}

function collectPipBreakdown(block) {
    if (!block) return [];
    const out = [];
    const push = (key, value, label) => {
        if (value > 0) out.push({ key, label, value });
    };
    push('generic', Number(block.generic || 0), 'Generic');
    push('x', Number(block.x || 0), 'X');
    push('hybrid', Number(block.hybrid || 0), 'Hybrid');
    push('phyrexian', Number(block.phyrexian || 0), 'Phyrexian');
    push('snow', Number(block.snow || 0), 'Snow');
    // `symbols` is keyed by color letter: { u: {count: 1}, r: {count: 2} }.
    const symbols = block.symbols || {};
    for (const letter of Object.keys(symbols)) {
        const count = Number(symbols[letter]?.count || 0);
        if (count > 0)
            out.push({
                key: `sym-${letter}`,
                label: letter.toUpperCase(),
                value: count,
            });
    }
    return out;
}

/**
 * Every signal behind one entry's score:
 *   - badges:  on/off flags
 *   - details: label/value pairs
 *   - sections: castability, activation, bias, and special adjustments,
 *     each present only when it carries data
 * A new backend field needs one entry here; the UI handles the layout.
 */
export function buildScoringSignals(entry) {
    const badges = [];
    const details = [];
    const sections = [];
    const bias = entry?.bias || {};
    const special = entry?.special || {};
    const castability = entry?.castability || {};
    const activation = entry?.activationCostScoring || {};

    // Boolean flags → badges
    const flag = (cond, key, label, tone, hint) => {
        if (cond) badges.push({ key, label, tone, hint });
    };
    if (entry?.hasAnySynergyDependencies) {
        badges.push({
            key: 'synergy',
            label: entry.hasComplexSynergyDependencies ? 'Complex synergy' : 'Synergy-dependent',
            tone: 'neutral',
            hint: entry.hasComplexSynergyDependencies
                ? 'Score leans on multiple other cards working together.'
                : 'Score depends on at least one other card interacting with this one.',
        });
    }
    flag(
        entry?.hasGenericKindredDependencies,
        'kindred',
        'Kindred-leaning',
        'neutral',
        'Score assumes typal / kindred support in the deck.'
    );
    flag(
        entry?.hasNoneBaseDependency,
        'base-dep',
        'Base dependency',
        'neutral',
        'Relies on a baseline effect elsewhere on the card.'
    );
    flag(
        entry?.isSingleTriggerOnly,
        'single-trigger',
        'Fires once',
        'watch',
        'Only one trigger in the card’s lifetime — limits compounding value.'
    );
    flag(
        entry?.allowSynergyAdjustment === false,
        'no-synergy-adj',
        'No synergy adjust',
        'watch',
        'Synergy post-processing is disabled for this entry.'
    );
    flag(
        special.isCoinFlip,
        'coinflip',
        'Coin flip',
        'watch',
        'Score adjusted for the 50/50 variance this effect introduces.'
    );
    flag(
        castability.hasFreeCast,
        'free-cast',
        'Free cast',
        'positive',
        'This card can be cast for no mana under some condition.'
    );
    flag(
        !castability.hasFreeCast && castability.hasFreeCastWithCaveat,
        'free-cast-caveat',
        'Free w/ caveat',
        'positive',
        'Free to cast with a qualifying condition.'
    );
    flag(
        castability.hasReductionEffect,
        'cost-reduction',
        'Cost reduction',
        'positive',
        'Reduces the mana cost of this or other spells.'
    );
    flag(
        castability.hasSpeedBonus,
        'speed-bonus',
        'Speed bonus',
        'positive',
        'Casts faster than its mana value would suggest (e.g. flash, instant-speed).'
    );
    flag(
        activation.hasRepeatableActivation,
        'repeatable-act',
        'Repeatable activation',
        'positive',
        'Ability can be activated more than once per turn.'
    );
    flag(
        activation.hasFreeCast,
        'free-activation',
        'Free activation',
        'positive',
        'Can be activated for no mana under some condition.'
    );
    flag(
        activation.hasReductionEffect,
        'activation-reduction',
        'Activation reduction',
        'positive',
        'Reduces the cost of this or another activation.'
    );
    flag(
        activation.hasSpeedBonus,
        'activation-speed',
        'Activation speed bonus',
        'positive',
        'Activation is faster than its cost implies.'
    );

    // Top-level summary details
    const preBias = Number(bias.scorePreBiasAdjustment || 0);
    const overall = Number(bias.overallBias ?? 1);
    if (preBias > 0 || overall !== 1) {
        details.push({
            key: 'bias',
            label: 'Bias',
            value: `${formatNumber(preBias, 1)} × ${overall.toFixed(2)} = ${formatNumber(preBias * overall, 1)}`,
            hint: 'Raw category score, multiplied by the overall bias derived from cost, pre-condition, activation, and upkeep factors.',
        });
    }
    if (bias.categoryBias != null && Number(bias.categoryBias) !== 1) {
        details.push({
            key: 'category-bias',
            label: 'Category weight',
            value: `×${Number(bias.categoryBias).toFixed(2)}`,
            hint: 'Multiplier the role applies before combining with castability / activation biases.',
        });
    }
    if (entry?.postProcessorScore != null && entry.postProcessorScore !== entry.score) {
        details.push({
            key: 'post-processor',
            label: 'Post-processed from',
            value: formatNumber(entry.postProcessorScore, 1),
            hint: 'Base score produced by the category processor before synergy / coin-flip adjustments.',
        });
    }
    if (entry?.scoreRating != null && Number(entry.scoreRating) !== Number(entry.score)) {
        details.push({
            key: 'score-rating',
            label: 'Score rating',
            value: formatNumber(entry.scoreRating, 2),
            hint: 'Alternate score used for ranking / comparison contexts.',
        });
    }

    // Castability sub-block
    const castabilityDetails = [];
    if (castability.cmc != null && Number(castability.cmc) > 0) {
        castabilityDetails.push({
            key: 'cast-cmc',
            label: 'Mana value',
            value: `${castability.cmc}${castability.hasFreeCast ? ' (free)' : ''}`,
        });
    }
    if (
        castability.totalForNonX != null &&
        Number(castability.totalForNonX) !== Number(castability.cmc || 0) &&
        Number(castability.totalForNonX) > 0
    ) {
        castabilityDetails.push({
            key: 'cast-nonx',
            label: 'Non-X cost',
            value: String(castability.totalForNonX),
        });
    }
    if (Number(castability.coloredPipCount || 0) > 0) {
        castabilityDetails.push({
            key: 'cast-colored',
            label: 'Colored pips',
            value: String(castability.coloredPipCount),
        });
    }
    if (castability.score != null && Number(castability.score) !== 0) {
        castabilityDetails.push({
            key: 'cast-score',
            label: 'Castability score',
            value: formatNumber(castability.score, 2),
        });
    }
    if (
        castability.baselineScore != null &&
        Number(castability.baselineScore) !== 0 &&
        Number(castability.baselineScore) !== Number(castability.score || 0)
    ) {
        castabilityDetails.push({
            key: 'cast-baseline',
            label: 'Baseline',
            value: formatNumber(castability.baselineScore, 2),
        });
    }
    const pipChips = collectPipBreakdown(castability);
    const castCostChips = collectAdditionalCostChips(castability, 'cast');
    if (castabilityDetails.length || pipChips.length || castCostChips.length) {
        sections.push({
            key: 'castability',
            title: 'Castability',
            details: castabilityDetails,
            pips: pipChips,
            chips: castCostChips,
        });
    }

    // Activation sub-block (activated abilities)
    const activationDetails = [];
    if (activation.cmc != null && Number(activation.cmc) > 0) {
        activationDetails.push({
            key: 'act-cmc',
            label: 'Cost',
            value: `${activation.cmc} mana`,
        });
    }
    if (Number(activation.coloredPipCount || 0) > 0) {
        activationDetails.push({
            key: 'act-colored',
            label: 'Colored pips',
            value: String(activation.coloredPipCount),
        });
    }
    const activationPips = collectPipBreakdown(activation);
    const activationCostChips = collectAdditionalCostChips(activation, 'act');
    if (activationDetails.length || activationPips.length || activationCostChips.length) {
        sections.push({
            key: 'activation',
            title: 'Activation',
            details: activationDetails,
            pips: activationPips,
            chips: activationCostChips,
        });
    }

    // Bias breakdown (every non-1 component)
    const biasDetails = [];
    for (const [field, label] of Object.entries(BIAS_COMPONENT_LABELS)) {
        const value = bias[field];
        if (value != null && Number(value) !== 1) {
            biasDetails.push({
                key: `bias-${field}`,
                label,
                value: `×${Number(value).toFixed(2)}`,
            });
        }
    }
    if (bias.castabilityBiasGenericCostDeduction != null && Number(bias.castabilityBiasGenericCostDeduction) !== 0) {
        biasDetails.push({
            key: 'bias-generic-deduction',
            label: 'Generic deduction',
            value: `−${Number(bias.castabilityBiasGenericCostDeduction).toFixed(2)}`,
        });
    }
    if (biasDetails.length)
        sections.push({
            key: 'bias',
            title: 'Bias breakdown',
            details: biasDetails,
        });

    // Coin-flip / special adjustments
    const specialDetails = [];
    if (special.preCoinFlipAdjustmentScore != null && special.isCoinFlip) {
        specialDetails.push({
            key: 'precoinflip-score',
            label: 'Pre-coin-flip score',
            value: formatNumber(special.preCoinFlipAdjustmentScore, 2),
        });
    }
    if (special.preCoinFlipAdjustmentMaxScore != null && special.isCoinFlip) {
        specialDetails.push({
            key: 'precoinflip-max',
            label: 'Pre-coin-flip max',
            value: formatNumber(special.preCoinFlipAdjustmentMaxScore, 2),
        });
    }
    if (entry?.maxScore != null && Number(entry.maxScore) !== Number(entry.score)) {
        specialDetails.push({
            key: 'max-score',
            label: 'Raw max',
            value: formatNumber(entry.maxScore, 2),
            hint: 'Score ceiling before post-processing / synergy adjustments.',
        });
    }
    if (specialDetails.length)
        sections.push({
            key: 'special',
            title: 'Adjustments',
            details: specialDetails,
        });

    return { badges, details, sections };
}

/** Everything one scoring entry renders, resolved against the card's parsed context. */
export function scoringEntryView(entry, context) {
    const resolved = resolveScoringContext(context, entry?.contextIds);
    const triggerText = resolved?.preCondition?.rawText || resolved?.preCondition?.text || null;
    const resultText = resolved?.condition?.rawText || resolved?.condition?.text || entry?.conditionText || null;
    const lineText = resolved?.line?.raw || null;
    return {
        score: Number(entry?.score || 0),
        effectLabel: effectTypeLabel(entry?.effectType),
        triggerText: triggerText && triggerText !== resultText ? triggerText : null,
        resultText,
        resultLabel:
            entry?.effectType === 'castingCost' ? 'Cost' : entry?.effectType === 'static' ? 'Effect' : 'Result',
        sourceLine: lineText && lineText !== resultText ? lineText : null,
        // The parsed action schema behind the entry — who it affects, what the
        // player and card do, which zones cards move between.
        chips: buildEffectSummary(resolved?.condition),
        ...buildScoringSignals(entry),
    };
}
